import React, { useEffect, useState } from 'react';
import { adminListChatSessions, adminFetchChatSession, adminDeleteChatSession } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { MessageSquare, Trash2, User, Bot, Loader2, RefreshCw, X } from 'lucide-react';

const LANG_BADGE = { en: 'EN', it: 'IT', hi: 'HI' };

const AdminChatTab = () => {
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminListChatSessions();
      setSessions(list);
    } catch (err) {
      toast({ title: 'Could not load chats' });
    } finally { setLoading(false); }
  };

  const openSession = async (sid) => {
    setSelected(sid);
    setDetailLoading(true);
    try {
      const res = await adminFetchChatSession(sid);
      setMessages(res.messages || []);
    } catch { toast({ title: 'Could not load conversation' }); }
    finally { setDetailLoading(false); }
  };

  const removeSession = async (sid) => {
    try {
      await adminDeleteChatSession(sid);
      setSessions((cur) => cur.filter((s) => s.session_id !== sid));
      if (selected === sid) { setSelected(null); setMessages([]); }
    } catch { toast({ title: 'Delete failed' }); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      <div className="lg:col-span-5 rounded-2xl border bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Chat sessions ({sessions.length})</div>
          <Button variant="outline" onClick={load} className="h-8">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {sessions.length === 0 && !loading && (
            <div className="px-4 py-10 text-center text-slate-500 text-sm">No chat conversations yet.</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => openSession(s.session_id)}
              className={`w-full text-left px-4 py-3 border-b hover:bg-orange-50 transition-colors ${
                selected === s.session_id ? 'bg-orange-50' : 'bg-white'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">{new Date(s.last_at).toLocaleString()}</div>
                <div className="flex items-center gap-1">
                  <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">{LANG_BADGE[s.lang] || 'EN'}</Badge>
                  <span className="text-[10px] text-slate-500">{s.count} msg</span>
                </div>
              </div>
              <div className="mt-1 text-sm text-slate-800 truncate">
                <span className="font-semibold text-slate-500 mr-1">{s.last_role === 'user' ? 'User:' : 'AI:'}</span>
                {s.last_message}
              </div>
              <div className="mt-1 text-[10px] text-slate-400 truncate">{s.session_id}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-7 rounded-2xl border bg-white overflow-hidden flex flex-col min-h-[560px]">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation to view messages</div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="text-xs text-slate-500 truncate max-w-[70%]">{selected}</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => removeSession(selected)} className="h-8 text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" onClick={() => { setSelected(null); setMessages([]); }} className="h-8">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {detailLoading && <div className="text-center text-slate-400 text-sm">Loading…</div>}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.role === 'user' ? 'bg-slate-900 text-white rounded-br-sm' : 'bg-white border border-slate-200 rounded-bl-sm shadow-sm'}`}>
                    <div className="flex items-center gap-1 text-[10px] opacity-70 mb-1">
                      {m.role === 'user' ? <User className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
                      {new Date(m.created_at).toLocaleString()}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                  </div>
                </div>
              ))}
              {!detailLoading && messages.length === 0 && (
                <div className="text-center text-slate-400 text-sm">No messages in this session.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminChatTab;
